import { goBack } from "assets/icons";
import styled from "@emotion/styled";
import { Card, Col, Form, message, Row, Typography } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CreateBankInput, FileDocumentDto } from "services/interface";
import MainCreateBank from "./MainCreateBank";
import CreateBankFooter from "./CreateProductFooter";
import { useCreateBankFactory, useGetLogoBank } from "hooks/useFactory";
import { useBankListTotals } from "hooks/useBankListTotals";
import { useGetMasterBankType } from "hooks/useMasterData";
import { filterDocumentsToActiveSlots, getSlotsForBankType } from "./bankDocumentConfig";

const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 12 },
    },
  };
const CreateBank = () => {
const navigate = useNavigate();
  const { data: dataLogo } = useGetLogoBank();
  const { data: dataBankType } = useGetMasterBankType();
  const { mutate: CreateBank, isPending } = useCreateBankFactory();
  const [form] = Form.useForm();
  const { activeBanks, isLunganek, isLoading: loadingTotals } = useBankListTotals();
  const atBankLimit = !isLunganek && activeBanks >= 1;
  const documentMapRef = useRef<Record<string, FileDocumentDto>>({});

  const handleDocumentChange = useCallback(
    (document_type: string, dto: FileDocumentDto | null) => {
      if (document_type === "__reset__") {
        documentMapRef.current = {};
        return;
      }
      if (dto === null) {
        delete documentMapRef.current[document_type];
      } else {
        documentMapRef.current[document_type] = dto;
      }
    },
    []
  );

  // default ประเภทบัญชีเป็น "นิติบุคคล" เมื่อ master data โหลดเสร็จ
  useEffect(() => {
    if (form.getFieldValue("bank_type_id") != null) return;
    const corporate = dataBankType?.data?.find(
      (t) => t.value === "corporate_account"
    );
    if (corporate) form.setFieldsValue({ bank_type_id: corporate.id });
  }, [dataBankType, form]);

  useEffect(() => {
    if (loadingTotals) return;
    if (atBankLimit) {
      message.warning("มีบัญชีที่เปิดใช้งานอยู่แล้ว ปิดการใช้งานก่อนจึงจะเพิ่มบัญชีใหม่ได้");
      navigate("/manage-bank");
    }
  }, [atBankLimit, loadingTotals, navigate]);

  const onFinish = (values: any) => {
    if (atBankLimit) {
      message.warning("มีบัญชีที่เปิดใช้งานอยู่แล้ว ปิดการใช้งานก่อนจึงจะเพิ่มบัญชีใหม่ได้");
      navigate("/manage-bank");
      return;
    }
    const currentDesc =
      dataBankType?.data?.find((t) => String(t.id) === String(values.bank_type_id))
        ?.description ?? "";
    const requiredSlots = getSlotsForBankType(currentDesc).filter((s) => s.required);
    const missingSlots = requiredSlots.filter(
      (s) => !documentMapRef.current[s.document_type]
    );
    if (missingSlots.length > 0) {
      message.warning(
        `กรุณาอัพโหลดเอกสาร: ${missingSlots.map((s) => s.label).join(", ")}`
      );
      return;
    }
    const { bank_key, account_name, account_no } = values;     
    const bank_name = (dataLogo?.find((item) => item.bank_key === bank_key))?.name;
    const bankInput: CreateBankInput = {
      bank_name: bank_name ?? '',
      account_name: account_name,
      account_no:   account_no,
      bank_key:     bank_key,
      bank_type_id: values.bank_type_id,
      document_list: filterDocumentsToActiveSlots(
        Object.values(documentMapRef.current),
        currentDesc
      ),
    }
    CreateBank(bankInput, {
      onSuccess: (data) => {
        if (data.status === "success") {
          message.success("เพิ่มบัญชีสำเร็จ");
          navigate('/manage-bank')
        }else {
          message.error(`เพิ่มบัญชีไม่สำเร็จ ${data.error_message?.[0]}`);
        }
      },
      onError: () => {
        message.error("เพิ่มบัญชีไม่สำเร็จ");
      },
    });
  };

  return (
    <Form form={form} {...formItemLayout} onFinish={onFinish}>
      <Row className="rowgap-vbox" gutter={[24, 0]} style={{ height: "100%" }}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/manage-bank");
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>เพิ่มบัญชี</Typography.Title>
              <RightIconWrapper></RightIconWrapper>
            </HeaderTabs>
            <WrapperAll>
              <MainCreateBank 
                form={form} 
                dataLogo={dataLogo}
                onDocumentChange={handleDocumentChange}
              />
            </WrapperAll>
          </Card>
        </Col>
      </Row>
      <CreateBankFooter loading={isPending} disabled={atBankLimit || loadingTotals} />
    </Form>
  );
};

export default CreateBank;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
